import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Play, Search, Star } from 'lucide-react';
import { useAllTopics } from '@/hooks/useAllTopics';
import { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { slugifyTerm } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface DbVideo {
  id: string;
  title: string;
  embedCode: string;
  featured: boolean;
  sortOrder: number;
}

const VideoCategoryPage = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { topics } = useAllTopics();
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useIsMobile();
  const [dbVideos, setDbVideos] = useState<DbVideo[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!categoryId) return;
    // Pull videos via junction table (multi-category aware) and merge with legacy primary category_id
    (async () => {
      const [junctionRes, primaryRes] = await Promise.all([
        supabase
          .from('video_categories')
          .select('videos:video_id(id, title, embed_code, featured, sort_order)')
          .eq('category_id', categoryId),
        supabase
          .from('videos')
          .select('id, title, embed_code, featured, sort_order')
          .eq('category_id', categoryId),
      ]);

      const map = new Map<string, DbVideo>();
      const addRow = (v: any) => {
        if (!v) return;
        map.set(v.id, {
          id: v.id,
          title: v.title,
          embedCode: v.embed_code,
          featured: !!v.featured,
          sortOrder: v.sort_order ?? 0,
        });
      };
      (junctionRes.data || []).forEach((r: any) => addRow(r.videos));
      (primaryRes.data || []).forEach(addRow);
      setDbVideos(Array.from(map.values()));
    })();
  }, [categoryId]);

  const topicData = topics.find(topic => topic.id === categoryId);

  const sortedVideos = useMemo(() => {
    const staticVids = topicData?.videos ?? [];
    const combined = [
      ...dbVideos.map((v) => ({ ...v, _featured: v.featured, _sort: v.sortOrder })),
      ...staticVids.map((v) => ({ ...v, _featured: false, _sort: 0, embedCode: v.embedCode })),
    ];
    return combined.sort((a: any, b: any) => {
      if (a._featured !== b._featured) return a._featured ? -1 : 1;
      if (a._sort !== b._sort) return (a._sort || 0) - (b._sort || 0);
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [topicData?.videos, dbVideos]);

  const filteredVideos = useMemo(() => {
    if (!searchTerm.trim()) return sortedVideos;
    return sortedVideos.filter(video =>
      video.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sortedVideos, searchTerm]);

  const filteredPlaceholderCount = useMemo(() => {
    if (!searchTerm.trim()) return topicData?.videoCount || 0;
    const placeholderVideos = Array.from({ length: topicData?.videoCount || 0 }, (_, index) =>
      `Video ${index + 1}`
    );
    return placeholderVideos.filter(title =>
      title.toLowerCase().includes(searchTerm.toLowerCase())
    ).length;
  }, [topicData?.videoCount, searchTerm]);

  if (!topicData) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-32 pb-20 bg-background">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-4">
              Category Not Found
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              The video category you're looking for doesn't exist.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const totalCount = (topicData.videoCount || 0) + dbVideos.length;

  return (
    <div className="min-h-screen relative">
      <Header />

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {!user && !loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm p-4">
          <Card className="w-full max-w-lg border-2 border-primary/30 shadow-2xl bg-background/95">
            <CardContent className="pt-6 pb-6 sm:pt-8 sm:pb-8 px-6 text-center space-y-5">
              <div className="space-y-2.5">
                <h2 className="text-2xl sm:text-3xl font-rockwell font-bold text-primary">
                  Private Members Only
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground">
                  You need to be logged in to access exclusive videos in this category
                </p>
                <p className="text-sm text-muted-foreground/80">
                  Don't have an account? Sign up for free!
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button onClick={() => navigate('/auth')} className="w-full sm:flex-1" size="default">
                  Login
                </Button>
                <Button onClick={() => navigate('/auth?tab=signup')} variant="outline" className="w-full sm:flex-1" size="default">
                  Join Free
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <main className="pt-32 pb-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-4">
              {topicData.title}
            </h1>
            {isMobile && (
              <p className="text-sm text-muted-foreground mb-4">{totalCount} videos available</p>
            )}
            <p className="text-xl text-muted-foreground mb-2">{topicData.description}</p>
            {!isMobile && (
              <p className="text-sm text-muted-foreground">{totalCount} videos available</p>
            )}
          </div>

          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                type="text"
                placeholder="Search videos in this category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedVideos.length > 0 ? (
              filteredVideos.map((video: any) => {
                const paddingMatch = video.embedCode.match(/padding:([^;]+)/);
                const aspectRatio = paddingMatch ? paddingMatch[1] : '56.25% 0 0 0';

                return (
                  <Card
                    key={video.id}
                    className="group hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/video/${categoryId}/${video.id}-${slugifyTerm(video.title || `video-${video.id}`)}`)}
                  >
                    <CardHeader className="relative">
                      <div className="flex items-start justify-between gap-2 mb-4">
                        {video.title && <h3 className="font-semibold text-lg">{video.title}</h3>}
                        {video._featured && (
                          <Badge variant="default" className="gap-1 shrink-0">
                            <Star className="h-3 w-3" />
                            Featured
                          </Badge>
                        )}
                      </div>
                      <div
                        className="bg-muted rounded-lg group-hover:bg-muted/80 transition-colors relative w-full pointer-events-none"
                        style={{ padding: aspectRatio }}
                      >
                        <div
                          className="absolute top-0 left-0 w-full h-full"
                          dangerouslySetInnerHTML={{ __html: video.embedCode }}
                        />
                      </div>
                    </CardHeader>
                  </Card>
                );
              })
            ) : (
              Array.from({ length: filteredPlaceholderCount }, (_, index) => {
                const videoNumber = searchTerm.trim()
                  ? Array.from({ length: topicData.videoCount }, (_, i) => i + 1)
                      .filter(num => `Video ${num}`.toLowerCase().includes(searchTerm.toLowerCase()))[index]
                  : index + 1;

                return (
                  <Card
                    key={index}
                    className="group hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/video/${categoryId}-video-${videoNumber}`)}
                  >
                    <CardHeader className="relative">
                      <div className="bg-muted rounded-lg h-48 flex items-center justify-center group-hover:bg-muted/80 transition-colors">
                        <div className="text-center">
                          <Play className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">Video {videoNumber}</p>
                          <p className="text-xs text-muted-foreground mt-1">Coming Soon</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <h3 className="font-semibold text-lg">Video {videoNumber}</h3>
                      <p className="text-sm text-muted-foreground">
                        Part of the {topicData.title} series
                      </p>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VideoCategoryPage;
